import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Badge,
  Box,
  Button,
  Card,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Icon,
  Input,
  Select,
  SimpleGrid,
  Spacer,
  Spinner,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useToast,
} from '@chakra-ui/react';
import { FaSyncAlt } from 'react-icons/fa';
import { FaFileExport } from 'react-icons/fa6';
import { FiAlertCircle, FiFilter, FiLink } from 'react-icons/fi';
import { toCNPJ, toCPF } from '@partiu/ui/formatters.ts';
import { useTheme } from '@partiu/ui/contexts/ThemeProvider.tsx';
import {
  CreateMapIntegrationPlanModal,
  CreateMapIntegrationPlanModalRef,
} from '../../PlanMapping/CreateMapIntegrationPlanModal.tsx';
import {
  DashboardHooks,
  useAllMapIntegrationPlans,
  useGenerateSyncClientsMetadataReportMutation,
  useSyncClientsMetadata,
} from '@partiu/api/dashboard/hooks.ts';
import {
  ISyncClientMetadata,
  ISyncClientSubscriptionMapped,
  ISyncClientsMetadataExportPayload,
} from '@partiu/api/dashboard/types.ts';
import { RequestParameters } from '@partiu/api/types';

type TriStateFilter = '' | 'true' | 'false';

interface SyncClientsFiltersState {
  active: TriStateFilter;
  synced: TriStateFilter;
  withoutSubscriptionsMapped: TriStateFilter;
  /** '' = todos; '__none__' = sem erro; texto = mensagem exata */
  errorMessage: string;
}

const DEFAULT_FILTERS: SyncClientsFiltersState = {
  active: 'true',
  synced: '',
  withoutSubscriptionsMapped: '',
  errorMessage: '',
};

const ERROR_NONE = '__none__';

function formatDate(dateStr?: string | null): string {
  if (!dateStr) return '-';
  const raw = String(dateStr).trim();
  if (!raw || raw.startsWith('0000-00-00') || raw === '0000-00-00 00:00:00') return '-';
  try {
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return raw;
    return d.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return raw;
  }
}

/** Sem data / indefindo = ativo; data no passado = vencido */
function isValidityActive(validUntil?: string | null): boolean {
  if (!validUntil) return true;
  const raw = String(validUntil).trim();
  if (!raw || raw.startsWith('0000-00-00')) return true;
  const until = new Date(raw);
  if (Number.isNaN(until.getTime())) return true;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  until.setHours(0, 0, 0, 0);
  return until >= today;
}

function formatDocument(doc?: string | null): string {
  if (!doc) return '-';
  const digits = doc.replace(/\D/g, '');
  if (digits.length === 14) return toCNPJ(digits);
  if (digits.length === 11) return toCPF(digits);
  return doc;
}

function formatNumber(num: number): string {
  return new Intl.NumberFormat('pt-BR').format(num);
}

function extractPageFromUrl(url: string | null | undefined): number | null {
  if (!url) return null;
  const match = url.match(/[?&]page=(\d+)/);
  return match ? parseInt(match[1], 10) : null;
}

function applyActiveFilters(
  search: string,
  filters: SyncClientsFiltersState
): Omit<ISyncClientsMetadataExportPayload, 'name'> {
  const payload: Omit<ISyncClientsMetadataExportPayload, 'name'> = {};

  if (search) payload.search = search;
  if (filters.active !== '') payload.active = filters.active === 'true';
  if (filters.synced !== '') payload.synced = filters.synced === 'true';
  if (filters.withoutSubscriptionsMapped !== '') {
    payload.withoutSubscriptionsMapped = filters.withoutSubscriptionsMapped === 'true';
  }
  if (filters.errorMessage !== '') payload.errorMessage = filters.errorMessage;

  return payload;
}

function buildListParams(
  currentPage: number,
  search: string,
  filters: SyncClientsFiltersState
): RequestParameters {
  const active = applyActiveFilters(search, filters);
  const params: RequestParameters = {
    page: currentPage,
    page_size: 10,
  };

  if (active.search) params.search = active.search;
  if (active.active !== undefined) params.active = active.active;
  if (active.synced !== undefined) params.synced = active.synced;
  if (active.withoutSubscriptionsMapped !== undefined) {
    params.without_subscriptions_mapped = active.withoutSubscriptionsMapped;
  }
  if (active.errorMessage !== undefined) params.error_message = active.errorMessage;

  return params;
}

function StatCard({
  label,
  value,
  scheme,
}: {
  label: string;
  value: number | null | undefined;
  scheme: string;
}) {
  return (
    <Box textAlign="center" p={3} borderRadius="lg" bg={`${scheme}.50`} border="1px solid" borderColor={`${scheme}.100`}>
      <Text fontSize="xs" color={`${scheme}.600`} fontWeight="semibold" mb={1} textTransform="uppercase" letterSpacing="wide">
        {label}
      </Text>
      <Text color={`${scheme}.600`} fontSize="xl" fontWeight="bold">
        {value == null ? '-' : formatNumber(value)}
      </Text>
    </Box>
  );
}

function SubscriptionsMappedCell({ subscriptions }: { subscriptions: ISyncClientSubscriptionMapped[] }) {
  if (!subscriptions?.length) {
    return (
      <Text fontSize="xs" color="gray.400">
        —
      </Text>
    );
  }

  return (
    <HStack spacing={1} flexWrap="wrap">
      {subscriptions.map((sub) => (
        <Tooltip
          key={`${sub.productId}-${sub.planId}-${sub.startDate}`}
          label={
            <Box>
              <Text>Produto: {sub.productName || `#${sub.productId}`}</Text>
              <Text>Plano: {sub.planName || `#${sub.planId}`}</Text>
              <Text>Início: {formatDate(sub.startDate)}</Text>
              <Text>Válido até: {sub.validUntil ? formatDate(sub.validUntil) : 'Indefinido'}</Text>
              <Text>Status: {sub.active ? 'Ativo' : 'Inativo'}</Text>
            </Box>
          }
          hasArrow
          placement="top"
        >
          <Badge
            colorScheme={sub.active ? 'blue' : 'gray'}
            variant="subtle"
            fontSize="2xs"
            cursor="help"
            maxW="140px"
            noOfLines={1}
          >
            {sub.productName || `#${sub.productId}`}
          </Badge>
        </Tooltip>
      ))}
    </HStack>
  );
}


function SyncClientDetailPanel({
  row,
  onCreateMapping,
  slugsByPlanId,
  mappedSlugs,
}: {
  row: ISyncClientMetadata;
  onCreateMapping: (slug: string) => void;
  slugsByPlanId: Map<number, string[]>;
  mappedSlugs: Set<string>;
}) {
  const subscriptions = row.subscriptionsMapped ?? [];
  const plans = row.plansMapped ?? [];

  return (
    <Box>
      {row.errorMessage && (
        <Alert status="error" variant="left-accent" borderRadius="md" mb={3} alignItems="flex-start">
          <AlertIcon mt={0.5} />
          <Box flex="1">
            <AlertTitle fontSize="sm" mb={1}>
              Erro na sincronização
            </AlertTitle>
            <AlertDescription fontSize="sm" lineHeight="short" whiteSpace="pre-wrap">
              {row.errorMessage}
            </AlertDescription>
          </Box>
        </Alert>
      )}

      {plans.length > 0 ? (
        <Box mb={3}>
          <HStack mb={2} spacing={2} align="center">
            <Text fontSize="sm" fontWeight="700" color="gray.700">
              Contratos encontrados
            </Text>
            <Badge colorScheme={row.synced ? 'green' : 'orange'} fontSize="2xs">
              {row.synced ? 'Sincronizado' : 'Não sincronizado'}
            </Badge>
          </HStack>
          <Table size="sm" variant="simple" bg="white" borderRadius={6} overflow="hidden">
            <Thead bg="gray.100">
              <Tr>
                <Th fontSize="xs" px={3} py={2.5} color="gray.600" fontWeight="700" letterSpacing="0.04em" textTransform="uppercase">
                  Identificador
                </Th>
                <Th fontSize="xs" px={3} py={2.5} color="gray.600" fontWeight="700" letterSpacing="0.04em" textTransform="uppercase">
                  Início
                </Th>
                <Th fontSize="xs" px={3} py={2.5} color="gray.600" fontWeight="700" letterSpacing="0.04em" textTransform="uppercase">
                  Validade
                </Th>
                <Th fontSize="xs" px={3} py={2.5} color="gray.600" fontWeight="700" letterSpacing="0.04em" textTransform="uppercase">
                  Status
                </Th>
                <Th fontSize="xs" px={3} py={2.5} color="gray.600" fontWeight="700" letterSpacing="0.04em" textTransform="uppercase">
                  Mapeamento
                </Th>
                <Th fontSize="xs" px={3} py={2.5} color="gray.600" fontWeight="700" letterSpacing="0.04em" textTransform="uppercase">
                  Ações
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {plans.map((plan) => {
                const active = isValidityActive(plan.validUntil);
                const hasMapping = Boolean(plan.slug) && mappedSlugs.has(plan.slug);
                const canMap = Boolean(plan.slug) && !hasMapping;
                return (
                  <Tr key={`${plan.slug}-${plan.startDate}`}>
                    <Td px={3} py={2} fontSize="xs">
                      {plan.slug || '-'}
                    </Td>
                    <Td px={3} py={2} fontSize="xs">
                      {formatDate(plan.startDate)}
                    </Td>
                    <Td px={3} py={2} fontSize="xs">
                      {plan.validUntil ? formatDate(plan.validUntil) : 'Indefinido'}
                    </Td>
                    <Td px={3} py={2}>
                      <Badge colorScheme={active ? 'green' : 'red'} fontSize="2xs">
                        {active ? 'Ativo' : 'Vencido'}
                      </Badge>
                    </Td>
                    <Td px={3} py={2}>
                      <Badge colorScheme={hasMapping ? 'green' : 'orange'} fontSize="2xs">
                        {hasMapping ? 'Mapeado' : 'Sem mapeamento'}
                      </Badge>
                    </Td>
                    <Td px={3} py={2}>
                      {canMap ? (
                        <Button
                          size="xs"
                          colorScheme="blue"
                          variant="outline"
                          leftIcon={<FiLink />}
                          onClick={() => onCreateMapping(plan.slug)}
                        >
                          Criar mapeamento
                        </Button>
                      ) : (
                        <Text fontSize="xs" color="gray.400">
                          —
                        </Text>
                      )}
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>
      ) : (
        <Box mb={3}>
          <HStack mb={2} spacing={2} align="center">
            <Text fontSize="sm" fontWeight="700" color="gray.700">
              Contratos encontrados
            </Text>
            <Badge colorScheme={row.synced ? 'green' : 'orange'} fontSize="2xs">
              {row.synced ? 'Sincronizado' : 'Não sincronizado'}
            </Badge>
          </HStack>
          <Text fontSize="xs" color="gray.400" fontStyle="italic">
            Nenhum contrato encontrado.
          </Text>
        </Box>
      )}

      {subscriptions.length > 0 ? (
        <Box>
          <Text fontSize="sm" fontWeight="700" color="gray.700" mb={2}>
            Assinaturas na Partiu
          </Text>
          <Table size="sm" variant="simple" bg="white" borderRadius={6} overflow="hidden">
            <Thead bg="gray.100">
              <Tr>
                <Th fontSize="xs" px={3} py={2.5} color="gray.600" fontWeight="700" letterSpacing="0.04em" textTransform="uppercase">
                  Produto
                </Th>
                <Th fontSize="xs" px={3} py={2.5} color="gray.600" fontWeight="700" letterSpacing="0.04em" textTransform="uppercase">
                  Plano
                </Th>
                <Th fontSize="xs" px={3} py={2.5} color="gray.600" fontWeight="700" letterSpacing="0.04em" textTransform="uppercase">
                  Slug (ERP)
                </Th>
                <Th fontSize="xs" px={3} py={2.5} color="gray.600" fontWeight="700" letterSpacing="0.04em" textTransform="uppercase">
                  Status
                </Th>
                <Th fontSize="xs" px={3} py={2.5} color="gray.600" fontWeight="700" letterSpacing="0.04em" textTransform="uppercase">
                  Início
                </Th>
                <Th fontSize="xs" px={3} py={2.5} color="gray.600" fontWeight="700" letterSpacing="0.04em" textTransform="uppercase">
                  Válido até
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {subscriptions.map((sub) => {
                const slugs = slugsByPlanId.get(sub.planId) ?? [];
                return (
                <Tr key={`${sub.productId}-${sub.planId}-${sub.startDate}`}>
                  <Td px={3} py={2} fontSize="xs">
                    <Text>{sub.productName || '-'}</Text>
                    <Text fontSize="2xs" color="gray.400" fontFamily="mono">
                      #{sub.productId}
                    </Text>
                  </Td>
                  <Td px={3} py={2} fontSize="xs">
                    <Text>{sub.planName || '-'}</Text>
                    <Text fontSize="2xs" color="gray.400" fontFamily="mono">
                      #{sub.planId}
                    </Text>
                  </Td>
                  <Td px={3} py={2}>
                    {slugs.length > 0 ? (
                      <HStack spacing={1} flexWrap="wrap">
                        {slugs.map((slug) => (
                          <Badge key={slug} colorScheme="purple" variant="subtle" fontSize="2xs" fontFamily="mono">
                            {slug}
                          </Badge>
                        ))}
                      </HStack>
                    ) : (
                      <Text fontSize="xs" color="gray.400">
                        —
                      </Text>
                    )}
                  </Td>
                  <Td px={3} py={2}>
                    <Badge colorScheme={sub.active ? 'green' : 'gray'} fontSize="2xs">
                      {sub.active ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </Td>
                  <Td px={3} py={2} fontSize="xs">
                    {formatDate(sub.startDate)}
                  </Td>
                  <Td px={3} py={2} fontSize="xs">
                    {sub.validUntil ? formatDate(sub.validUntil) : 'Indefinido'}
                  </Td>
                </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>
      ) : (
        <Text fontSize="xs" color="gray.400" fontStyle="italic">
          Nenhuma assinatura vinculada.
        </Text>
      )}
    </Box>
  );
}

export function SyncClientsMetadataTab() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { id: agreementId } = useTheme();
  const createMappingModalRef = useRef<CreateMapIntegrationPlanModalRef>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [expandedIndexes, setExpandedIndexes] = useState<number[]>([]);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<SyncClientsFiltersState>(DEFAULT_FILTERS);

  const generateReportMutation = useGenerateSyncClientsMetadataReportMutation({
    onSuccess: () => {
      queryClient.invalidateQueries([DashboardHooks.Reports]).catch(console.error);
      toast({
        title: 'Sucesso',
        description: 'Relatório gerado com sucesso. Quando estiver pronto você poderá baixar no menu relatórios',
        status: 'success',
      });
    },
    onError: () => {
      toast({
        title: 'Erro',
        description: 'Não foi possível gerar o relatório. Tente novamente mais tarde.',
        status: 'error',
      });
    },
  });

  const handleGenerateReport = useCallback(() => {
    const now = new Date();
    const payload: ISyncClientsMetadataExportPayload = {
      name: `Clientes sync ERP - ${now.toLocaleDateString('pt-BR')} ${now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`,
      ...applyActiveFilters(search, filters),
    };
    generateReportMutation.mutate(payload);
  }, [generateReportMutation, search, filters]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setSearch(searchInput.trim());
      setCurrentPage(1);
      setExpandedIndexes([]);
    }, 500);

    return () => clearTimeout(timeout);
  }, [searchInput]);

  const handleCreateMapping = useCallback((slug: string) => {
    createMappingModalRef.current?.open({ plan_slug_integration: slug });
  }, []);

  const handleMappingCreated = useCallback(() => {
    queryClient.refetchQueries([DashboardHooks.SyncClientsMetadata]).catch(console.error);
  }, [queryClient]);

  const handleFilterChange = useCallback((key: keyof SyncClientsFiltersState, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
    setExpandedIndexes([]);
  }, []);

  const listParams = useMemo(
    () => buildListParams(currentPage, search, filters),
    [currentPage, search, filters]
  );

  const { data, isLoading } = useSyncClientsMetadata(listParams);
  const { data: allMappings } = useAllMapIntegrationPlans();

  /** planId -> slugs do ERP mapeados nele (a API não devolve slug por assinatura) */
  const slugsByPlanId = useMemo(() => {
    const map = new Map<number, string[]>();
    (allMappings ?? []).forEach((mapping) => {
      if (!mapping.plan_slug_integration) return;
      const current = map.get(mapping.plan) ?? [];
      if (!current.includes(mapping.plan_slug_integration)) {
        current.push(mapping.plan_slug_integration);
      }
      map.set(mapping.plan, current);
    });
    return map;
  }, [allMappings]);

  /** Slugs do ERP que já têm mapeamento — fonte de verdade para "criar caso não exista" */
  const mappedSlugs = useMemo(
    () =>
      new Set(
        (allMappings ?? [])
          .map((mapping) => mapping.plan_slug_integration)
          .filter((slug): slug is string => Boolean(slug))
      ),
    [allMappings]
  );

  const statistics = data?.statistics;
  const errorMessages = statistics?.errorMessages ?? [];

  const subscriptionsPerProduct = useMemo(
    () => [...(statistics?.totalActiveSubscriptionsPerProduct ?? [])].sort((a, b) => b.count - a.count),
    [statistics?.totalActiveSubscriptionsPerProduct]
  );

  const handlePrevious = useCallback(() => {
    if (!data?.previous) return;
    // A URL de `previous` da página 2 não traz `page` (o DRF omite page=1)
    setCurrentPage(extractPageFromUrl(data.previous) ?? 1);
    setExpandedIndexes([]);
  }, [data?.previous]);

  const handleNext = useCallback(() => {
    const p = extractPageFromUrl(data?.next);
    if (p !== null) {
      setCurrentPage(p);
      setExpandedIndexes([]);
    }
  }, [data?.next]);

  const rows = data?.results || [];
  const isInvalidSync = data?.syncStatus === 'error';

  return (
    <Box p={4}>
      <Card p={4} borderRadius={12} boxShadow="sm">
        <HStack mb={3} spacing={2}>
          <Icon as={FaSyncAlt} color="blue.500" />
          <Box>
            <Text fontSize="sm" fontWeight="600" color="gray.700">
              Clientes da sincronização
            </Text>
            <Text fontSize="xs" color="gray.500">
              Resultado da última sync com o ERP
            </Text>
          </Box>
          <Spacer />
          <Button
            size="sm"
            colorScheme="green"
            variant="outline"
            leftIcon={<FaFileExport />}
            onClick={handleGenerateReport}
            isLoading={generateReportMutation.isLoading}
            loadingText="Gerando..."
          >
            Exportar CSV
          </Button>
        </HStack>

        {/* {statistics && (
          <>
            <SimpleGrid columns={{ base: 2, md: 3, xl: 6 }} spacing={3} mb={3}>
              <StatCard label="Total de clientes" value={statistics.totalClients} scheme="cyan" />
              <StatCard label="Ativos" value={statistics.totalClientsActive} scheme="green" />
              <StatCard label="Inativos" value={statistics.totalClientsInactive} scheme="red" />
              <StatCard label="Sincronizados" value={statistics.totalClientsSynced} scheme="blue" />
              <StatCard label="Não sincronizados" value={statistics.totalClientsNotSynced} scheme="orange" />
              <StatCard label="Com plano mapeado" value={statistics.totalClientsMapped} scheme="purple" />
            </SimpleGrid>

            <Box p={3} mb={4} borderRadius="lg" bg="teal.50" border="1px solid" borderColor="teal.100">
              <Flex align="center" gap={4} wrap="wrap">
                <Box minW="150px">
                  <Text fontSize="xs" color="teal.600" fontWeight="semibold" textTransform="uppercase" letterSpacing="wide">
                    Assinaturas ativas
                  </Text>
                  <Text color="teal.600" fontSize="xl" fontWeight="bold">
                    {statistics.totalActiveSubscriptions == null
                      ? '-'
                      : formatNumber(statistics.totalActiveSubscriptions)}
                  </Text>
                </Box>
                {subscriptionsPerProduct.length > 0 && (
                  <>
                    <Box alignSelf="stretch" w="1px" bg="teal.100" display={{ base: 'none', md: 'block' }} />
                    <SimpleGrid flex={1} minW="220px" columns={{ base: 1, md: 2, xl: 3 }} spacing={2}>
                      {subscriptionsPerProduct.map((item) => (
                        <Flex
                          key={item.productId}
                          align="center"
                          justify="space-between"
                          gap={2}
                          bg="white"
                          border="1px solid"
                          borderColor="teal.100"
                          borderRadius="md"
                          px={3}
                          py={2}
                        >
                          <Tooltip label={`#${item.productId}`} hasArrow placement="top">
                            <Text fontSize="xs" color="gray.600" noOfLines={1} cursor="help">
                              {item.productName || `Produto #${item.productId}`}
                            </Text>
                          </Tooltip>
                          <Text fontSize="sm" fontWeight="bold" color="teal.600" flexShrink={0}>
                            {formatNumber(item.count)}
                          </Text>
                        </Flex>
                      ))}
                    </SimpleGrid>
                  </>
                )}
              </Flex>
            </Box>
          </>
        )} */}

        <Box bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius="lg" p={4} mb={4}>
          <HStack mb={3} spacing={2}>
            <Icon as={FiFilter} color="blue.500" />
            <Heading size="sm" color="gray.700">
              Filtros
            </Heading>
          </HStack>

          <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={4}>
            <FormControl>
              <FormLabel fontSize="xs" color="gray.600" mb={1}>
                Busca
              </FormLabel>
              <Input
                placeholder="CPF/CNPJ, telefone ou e-mail"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                bg="white"
                variant="filled"
              />
            </FormControl>

            <FormControl
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              bg="white"
              borderRadius="md"
              px={3}
              h="40px"
              alignSelf="end"
            >
              <FormLabel htmlFor="filter-active" mb={0} fontSize="xs" color="gray.600">
                Cliente ativo
              </FormLabel>
              <Switch
                id="filter-active"
                colorScheme="green"
                isChecked={filters.active === 'true'}
                onChange={(e) => handleFilterChange('active', e.target.checked ? 'true' : 'false')}
              />
            </FormControl>

            <FormControl>
              <FormLabel fontSize="xs" color="gray.600" mb={1}>
                Sincronização
              </FormLabel>
              <Select
                value={filters.synced}
                onChange={(e) => handleFilterChange('synced', e.target.value as TriStateFilter)}
                bg="white"
                variant="filled"
              >
                <option value="">Todos</option>
                <option value="true">Sincronizados</option>
                <option value="false">Não sincronizados</option>
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="xs" color="gray.600" mb={1}>
                Assinaturas
              </FormLabel>
              <Select
                value={filters.withoutSubscriptionsMapped}
                onChange={(e) =>
                  handleFilterChange('withoutSubscriptionsMapped', e.target.value as TriStateFilter)
                }
                bg="white"
                variant="filled"
              >
                <option value="">Todas</option>
                <option value="true">Sem assinaturas</option>
                <option value="false">Com assinaturas</option>
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="xs" color="gray.600" mb={1}>
                Erros de sincronização
              </FormLabel>
              <Select
                value={filters.errorMessage}
                onChange={(e) => handleFilterChange('errorMessage', e.target.value)}
                bg="white"
                variant="filled"
              >
                <option value="">Todos</option>
                <option value={ERROR_NONE}>Sem erro</option>
                {errorMessages.map((msg) => (
                  <option key={msg} value={msg}>
                    {msg}
                  </option>
                ))}
              </Select>
            </FormControl>
          </SimpleGrid>
        </Box>

        {isLoading && (
          <Flex align="center" justify="center" py={10}>
            <Spinner size="lg" color="blue.500" mr={3} />
            <Text color="gray.500" fontSize="sm">
              Carregando...
            </Text>
          </Flex>
        )}

        {!isLoading && isInvalidSync && (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <Box>
              <AlertTitle fontSize="sm">Sincronização indisponível</AlertTitle>
              <AlertDescription fontSize="xs">{data?.message}</AlertDescription>
            </Box>
          </Alert>
        )}

        {!isLoading && !isInvalidSync && rows.length === 0 && (
          <Text fontSize="sm" color="gray.500" textAlign="center" py={8}>
            Nenhum cliente encontrado com os filtros atuais.
          </Text>
        )}

        {!isLoading && !isInvalidSync && rows.length > 0 && (
          <>
            <Flex
              display={{ base: 'none', md: 'flex' }}
              align="center"
              gap={2}
              px={3}
              py={2.5}
              bg="gray.100"
              borderRadius="md"
              mb={2}
            >
              <Text
                w="140px"
                flexShrink={0}
                fontSize="xs"
                fontWeight="700"
                color="gray.600"
                letterSpacing="0.04em"
                textTransform="uppercase"
              >
                Documento
              </Text>
              <Text
                w="90px"
                flexShrink={0}
                fontSize="xs"
                fontWeight="700"
                color="gray.600"
                letterSpacing="0.04em"
                textTransform="uppercase"
              >
                Status
              </Text>
              <Text
                w="130px"
                flexShrink={0}
                fontSize="xs"
                fontWeight="700"
                color="gray.600"
                letterSpacing="0.04em"
                textTransform="uppercase"
              >
                Sincronização
              </Text>
              <Text
                flex={1}
                minW="160px"
                fontSize="xs"
                fontWeight="700"
                color="gray.600"
                letterSpacing="0.04em"
                textTransform="uppercase"
              >
                Erro
              </Text>
              <Text
                w="150px"
                flexShrink={0}
                fontSize="xs"
                fontWeight="700"
                color="gray.600"
                letterSpacing="0.04em"
                textTransform="uppercase"
              >
                Assinaturas
              </Text>
              <Box w="24px" flexShrink={0} />
            </Flex>

            <Accordion
              allowMultiple
              index={expandedIndexes}
              onChange={(expanded) => setExpandedIndexes(expanded as number[])}
              reduceMotion
            >
              {rows.map((row, idx) => (
                <AccordionItem
                  key={row.id}
                  mb={1.5}
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius={6}
                  overflow="hidden"
                >
                  <AccordionButton py={2.5} px={3} _hover={{ bg: 'gray.50' }}>
                    <Flex
                      flex="1"
                      align="center"
                      gap={2}
                      wrap={{ base: 'wrap', md: 'nowrap' }}
                      textAlign="left"
                    >
                      <Text fontSize="sm" fontWeight="500" w={{ base: 'auto', md: '140px' }} flexShrink={0} noOfLines={1}>
                        {formatDocument(row.document)}
                      </Text>
                      <Box w={{ base: 'auto', md: '90px' }} flexShrink={0}>
                        <Badge colorScheme={row.active ? 'green' : 'red'} fontSize="2xs">
                          {row.active ? 'Ativo' : 'Inativo'}
                        </Badge>
                      </Box>
                      <Box w={{ base: 'auto', md: '130px' }} flexShrink={0}>
                        <Badge colorScheme={row.synced ? 'green' : 'orange'} fontSize="2xs">
                          {row.synced ? 'Sincronizado' : 'Não sincronizado'}
                        </Badge>
                      </Box>
                      <Box flex={1} minW={{ base: '100%', md: '160px' }}>
                        {row.errorMessage ? (
                          <Tooltip label={row.errorMessage} hasArrow placement="top">
                            <HStack spacing={1.5} cursor="help" align="flex-start">
                              <Icon as={FiAlertCircle} boxSize={3.5} color="red.500" mt={0.5} flexShrink={0} />
                              <Text fontSize="xs" color="red.600" noOfLines={2} lineHeight="short">
                                {row.errorMessage}
                              </Text>
                            </HStack>
                          </Tooltip>
                        ) : (
                          <Text fontSize="xs" color="gray.400">
                            —
                          </Text>
                        )}
                      </Box>
                      <Box w={{ base: 'auto', md: '150px' }} flexShrink={0}>
                        <SubscriptionsMappedCell subscriptions={row.subscriptionsMapped ?? []} />
                      </Box>
                    </Flex>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel pb={3} px={3} bg="gray.50">
                    {expandedIndexes.includes(idx) && (
                      <SyncClientDetailPanel
                        row={row}
                        onCreateMapping={handleCreateMapping}
                        slugsByPlanId={slugsByPlanId}
                        mappedSlugs={mappedSlugs}
                      />
                    )}
                  </AccordionPanel>
                </AccordionItem>
              ))}
            </Accordion>

            <HStack spacing={2} justifyContent="space-between" pt={4}>
              <Button
                onClick={handlePrevious}
                isDisabled={!data?.previous}
                colorScheme="blue"
                variant="outline"
                size="sm"
              >
                ← Anterior
              </Button>
              <Text fontSize="xs" color="gray.500">
                Página {currentPage}
              </Text>
              <Button
                onClick={handleNext}
                isDisabled={!data?.next}
                colorScheme="blue"
                variant="outline"
                size="sm"
              >
                Próximo →
              </Button>
            </HStack>
          </>
        )}
      </Card>

      <CreateMapIntegrationPlanModal
        ref={createMappingModalRef}
        agreementId={agreementId ?? undefined}
        onCreated={handleMappingCreated}
      />
    </Box>
  );
}
